import React from 'react'
import { sdk } from '@/lib/sdk'
import type { SignedMediaUrl } from '@/lib/sdk-official/resources/media'

/** Links last one hour; ask again a minute before so a player never holds a dead one. */
const RENEW_MARGIN_MS = 60_000
const BATCH = 100

const cache = new Map<string, SignedMediaUrl>()

/** The soonest moment this entry's links stop working (the MP4 link or the HLS signature). */
const expiryOf = (entry: SignedMediaUrl): number => {
  const times = [entry.expiresAt, entry.hls?.expiresAt].filter(Boolean).map((v) => Date.parse(v as string))
  return times.length ? Math.min(...times) : 0
}

const isFresh = (entry: SignedMediaUrl | undefined): entry is SignedMediaUrl =>
  !!entry?.url && expiryOf(entry) - Date.now() > RENEW_MARGIN_MS

type Waiter = { resolve: (entry: SignedMediaUrl) => void }

/** Ids asked for in the same tick travel together, so a list of cards costs one request, not one per card. */
const queue = new Map<string, Waiter[]>()
let flushTimer: ReturnType<typeof setTimeout> | undefined

const flush = async () => {
  flushTimer = undefined
  const batch = new Map(queue)
  queue.clear()
  const ids = [...batch.keys()]
  const answers: Record<string, SignedMediaUrl> = {}
  if (ids.length === 1) {
    try {
      answers[ids[0]] = await sdk.media.getUrl(ids[0])
    } catch (error) {
      const status = (error as { response?: { status?: number } })?.response?.status
      answers[ids[0]] = { id: ids[0], error: status === 410 ? 'removed' : status === 404 ? 'notFound' : 'forbidden' }
    }
  } else {
    for (let i = 0; i < ids.length; i += BATCH) {
      const chunk = ids.slice(i, i + BATCH)
      try {
        for (const entry of await sdk.media.getUrls(chunk)) answers[entry.id] = entry
      } catch {
        for (const id of chunk) answers[id] = { id, error: 'forbidden' }
      }
    }
  }
  for (const [id, waiters] of batch) {
    const entry = answers[id] ?? { id, error: 'notFound' as const }
    if (entry.url) cache.set(id, entry)
    else cache.delete(id)
    waiters.forEach((w) => w.resolve(entry))
  }
}

const request = (id: string) =>
  new Promise<SignedMediaUrl>((resolve) => {
    queue.set(id, [...(queue.get(id) ?? []), { resolve }])
    if (!flushTimer) flushTimer = setTimeout(flush, 10)
  })

/**
 * Signed links for these media ids. Fresh links come from memory; the rest are asked for together
 * (`GET /media/{id}/url` for one, `POST /media/urls` for several, up to 100 per call).
 * Links are never stored beyond this module.
 */
export const resolveSignedMedia = async (ids: string[], force = false): Promise<Record<string, SignedMediaUrl>> => {
  const result: Record<string, SignedMediaUrl> = {}
  const pending: Array<Promise<void>> = []
  for (const id of new Set(ids)) {
    const hit = cache.get(id)
    if (!force && isFresh(hit)) result[id] = hit
    else pending.push(request(id).then((entry) => { result[id] = entry }))
  }
  await Promise.all(pending)
  return result
}

/**
 * The signed links of a set of media, renewed on their own before they expire. `refresh` forces new
 * links (call it when an `<img>` or `<video>` fails to load). Ids that are empty are skipped.
 */
export const useSignedMedia = (ids: Array<string | null | undefined>) => {
  const key = [...new Set(ids.filter((id): id is string => !!id))].sort().join(',')
  const [links, setLinks] = React.useState<Record<string, SignedMediaUrl>>({})
  const [loading, setLoading] = React.useState(!!key)
  const [tick, setTick] = React.useState(0)
  const forceRef = React.useRef(false)
  const lastRefreshRef = React.useRef(0)

  React.useEffect(() => {
    if (!key) { setLinks({}); setLoading(false); return }
    let cancelled = false
    let timer: ReturnType<typeof setTimeout> | undefined
    const force = forceRef.current
    forceRef.current = false
    setLoading(true)
    resolveSignedMedia(key.split(','), force).then((resolved) => {
      if (cancelled) return
      setLinks(resolved)
      setLoading(false)
      const expiries = Object.values(resolved).map(expiryOf).filter(Boolean)
      if (expiries.length) {
        const wait = Math.max(Math.min(...expiries) - Date.now() - RENEW_MARGIN_MS, 5_000)
        timer = setTimeout(() => setTick((n) => n + 1), wait)
      }
    })
    return () => { cancelled = true; if (timer) clearTimeout(timer) }
  }, [key, tick])

  /** At most once every 10 s: a file that cannot be decoded must not loop requests. */
  const refresh = React.useCallback(() => {
    if (Date.now() - lastRefreshRef.current < 10_000) return
    lastRefreshRef.current = Date.now()
    forceRef.current = true
    setTick((n) => n + 1)
  }, [])

  return { links, loading, refresh }
}

interface HostedImage {
  mediaId?: string | null
  imageUrl?: string | null
  thumbnailUrl?: string | null
}

/**
 * The URL to render for an image. A hosted image (`mediaId`) uses its signed link, the thumbnail
 * for `thumb`; an older image keeps the public URL it always had. `hosted` tells the caller not to
 * run the URL through the image CDN rewrite, which only knows public files.
 */
export const imageSource = (
  image: HostedImage,
  links: Record<string, SignedMediaUrl>,
  variant: 'thumb' | 'full' = 'full',
): { src: string | undefined; hosted: boolean } => {
  if (image.mediaId) {
    const link = links[image.mediaId]
    return { src: variant === 'thumb' ? link?.thumbnailUrl || link?.url : link?.url, hosted: true }
  }
  return { src: (variant === 'thumb' ? image.thumbnailUrl || image.imageUrl : image.imageUrl) || undefined, hosted: false }
}
